import { useEffect, useRef, useState } from "react";
import { Loader2, Play, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { userProfileService } from "@/services/userProfileService";
import {
  resolveTtsVoiceId,
  ttsService,
  voiceDisplayName,
  type TtsVoiceOption,
} from "@/services/ttsService";

const SPEED_OPTIONS = [0.75, 1.0, 1.25, 1.5, 2.0];
const TEST_SENTENCE = "This is a preview of your selected voice in Deeply Smart.";

export function VoicePreferencesCard() {
  const { toast } = useToast();
  const [voices, setVoices] = useState<TtsVoiceOption[]>([]);
  const [voicesLoading, setVoicesLoading] = useState(true);
  const [voiceId, setVoiceId] = useState("");
  const [speed, setSpeed] = useState(1.0);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const testAudioRef = useRef<HTMLAudioElement | null>(null);
  const testUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [voiceList, profile] = await Promise.all([
          ttsService.getVoices(),
          userProfileService.getUserProfile(),
        ]);
        if (cancelled) return;
        setVoices(voiceList);
        const prefs = (profile?.preferences ?? {}) as Record<string, unknown>;
        const savedVoiceId = resolveTtsVoiceId(prefs);
        if (savedVoiceId) setVoiceId(savedVoiceId);
        else if (voiceList.length > 0) setVoiceId(voiceList[0].voiceId);
        if (typeof prefs.ttsSpeechRate === "number") setSpeed(prefs.ttsSpeechRate);
      } catch (err) {
        console.error("Failed to load voice preferences:", err);
      } finally {
        if (!cancelled) setVoicesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (testAudioRef.current) testAudioRef.current.pause();
      if (testUrlRef.current) URL.revokeObjectURL(testUrlRef.current);
    };
  }, []);

  const stopTestAudio = () => {
    if (testAudioRef.current) {
      testAudioRef.current.pause();
      testAudioRef.current = null;
    }
    if (testUrlRef.current) {
      URL.revokeObjectURL(testUrlRef.current);
      testUrlRef.current = null;
    }
  };

  const handleTestVoice = async () => {
    if (!voiceId || testing) return;
    setTesting(true);
    stopTestAudio();
    try {
      const selected = voices.find((v) => v.voiceId === voiceId);
      let audio: HTMLAudioElement;
      if (selected?.previewUrl) {
        audio = new Audio(selected.previewUrl);
      } else {
        const blob = await ttsService.synthesize(TEST_SENTENCE, voiceId);
        const url = URL.createObjectURL(blob);
        testUrlRef.current = url;
        audio = new Audio(url);
      }
      testAudioRef.current = audio;
      audio.playbackRate = speed;
      audio.onended = () => stopTestAudio();
      await audio.play();
    } catch (err) {
      toast({
        title: "Voice test failed",
        description: err instanceof Error ? err.message : "Could not play the sample.",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await ttsService.savePreferences({
        ttsVoiceId: voiceId || undefined,
        ttsSpeechRate: speed,
      });
      toast({
        title: "Voice preferences saved",
        description: "Read-aloud will use your selected voice and speed.",
      });
    } catch (err) {
      toast({
        title: "Save failed",
        description: err instanceof Error ? err.message : "Could not save voice preferences.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-2">
        <Volume2 className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Voice Preferences</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Choose the voice and speed used when reading AI responses aloud
      </p>

      {voicesLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading voices...
        </div>
      ) : voices.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Premium voices are not configured on this server. Read-aloud uses your browser&apos;s
          built-in voices — you can pick one from the playback controls in chat.
        </p>
      ) : (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tts-default-voice">Default Voice</Label>
              <select
                id="tts-default-voice"
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="w-full p-2 text-sm rounded-md border bg-background"
                data-testid="select-default-voice"
              >
                {voices.map((voice) => (
                  <option key={voice.voiceId} value={voice.voiceId}>
                    {voiceDisplayName(voice)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Powered by Featherless Muyan-TTS-SFT (podcast-quality read-aloud).
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tts-playback-speed">Playback Speed</Label>
              <select
                id="tts-playback-speed"
                value={String(speed)}
                onChange={(e) => setSpeed(Number(e.target.value))}
                className="w-full p-2 text-sm rounded-md border bg-background"
                data-testid="select-playback-speed"
              >
                {SPEED_OPTIONS.map((option) => (
                  <option key={option} value={String(option)}>
                    {option}×
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleTestVoice}
              disabled={!voiceId || testing}
              data-testid="button-test-voice"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Test Voice
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="sm:ml-auto"
              data-testid="button-save-voice-preferences"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Voice Preferences"
              )}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
